'use client'

// The octopus, as the pond shows it: three arms breaking the lake and
// swaying like the water is thinking.
//
// The art is Sam's octopus.glb. That file ships three separately rigged
// tentacles (3 meshes, 3 skins, 27 bones each, 3 baked 2.5s animations,
// 246KB) which is three copies of the same arm plus a loop short enough
// to notice. So scripts/extract-tentacle.mjs bakes ONE of them into
// tentacle.glb: the skin is used to straighten the sculpt into a
// canonical arm lying along +Y from 0 to 1, then the bones, the skin and
// the animation are thrown away. 18.6KB, and the taper, the silhouette
// and the sucker band all survive.
//
// From there it is the same deal as the wind debris: one instanced draw
// call per material, every arm bent along its own animated cubic bezier
// in the TSL vertex stage, everything that differs between arms hashed
// from instanceIndex. The CPU does nothing per frame; the instance
// matrices are never touched.

import { useMemo } from 'react'
import * as THREE from 'three/webgpu'
import { useGLTF } from '@react-three/drei'
import {
  Fn, varying, instanceIndex, hash, time, uniform,
  positionGeometry, normalLocal,
  float, vec2, vec3, sin, cos, mix, pow, smoothstep, clamp,
  normalize, cross,
} from 'three/tsl'

const COUNT = 3
const TAU = 6.28318

// The lake plane (world/Lake.jsx): centre [-73.65, -0.32, -0.68], 40x45.
// Its surface is world y -0.32. The visitor entity anchors on the BANK
// (the pond POI, where he can stand to speak), so the creature itself is
// shifted out into the water: he wishes from the edge, the arms answer
// from the pond.
const WATER_WORLD_Y = -0.32
const POND_SHIFT = { x: -2.5, z: -17.0 }

// How far under the surface the arms root, so they emerge from water
// rather than standing on whatever the basin floor is doing.
const ROOT_DEPTH = 0.55

// Suckers sit on +X of the canonical mesh (measured: the Tentecle-Under
// primitive's mid-section centroid is +0.0073 in X). The frame below is
// built so +X faces the INSIDE of the curl, which is where an octopus
// keeps them.
const SUCKERS_INSIDE = true

// Where arm i stands and how it is built. All hashed, so the arm shader
// and the ripple shader agree without a single buffer.
function armParams(i) {
  // A spoke each plus a jitter, rather than a free hash: three random
  // angles can and did land two arms on top of each other, and an even
  // spread is the only thing that guarantees they read as separate
  // animals coming out of the water.
  const a0 = float(i).mul(TAU / COUNT).add(hash(i.add(1)).mul(0.8))
  const ringR = float(1.25).add(hash(i.add(2)).mul(1.15))
  const base = vec3(
    cos(a0).mul(ringR).add(POND_SHIFT.x),
    0,
    sin(a0).mul(ringR).add(POND_SHIFT.z),
  )
  const len = float(2.2).add(hash(i.add(3)).mul(1.5))
  const thick = float(0.85).add(hash(i.add(4)).mul(0.4))
  const phase = hash(i.add(5)).mul(TAU)
  const leanA = hash(i.add(6)).mul(TAU)
  // How theatrical this arm is: low arch = mostly upright with a curled
  // tip, high arch = the full sea-serpent bow.
  const arch = float(0.25).add(hash(i.add(7)).mul(0.85))
  const hook = float(0.3).add(hash(i.add(8)).mul(0.95))
  // Its own tempo, so no two arms ever settle into the same rhythm.
  const rate = float(0.75).add(hash(i.add(9)).mul(0.7))
  return { base, len, thick, phase, dir: vec2(cos(leanA), sin(leanA)), arch, hook, rate }
}

export function OctopusTentacles({ groundY = 0 }) {
  const gltf = useGLTF('/models/tentacle.glb')

  const { parts, uWaterLocal, ringGeometry, ringMaterial } = useMemo(() => {
    const uWaterLocal = uniform(0)

    // Shared bend. Both materials run the identical vertex program, so
    // the sucker band stays welded to the body it was sculpted on.
    const makeBend = (vNrm) => Fn(() => {
      const i = instanceIndex.toVar()
      const { base, len, thick, phase, dir, arch, hook, rate } = armParams(i)

      // The mesh is baked 0..1 along +Y, so its own Y is the curve
      // parameter. No remapping, no length uniform to keep in sync.
      const t = clamp(positionGeometry.y, 0.0, 1.0).toVar()
      const tm = time.mul(rate).toVar()

      // Rhythms that never share a period, so the motion cannot settle
      // into a loop, plus a slow yaw of the whole arm like it is feeling
      // sideways through the water.
      const s1 = sin(tm.mul(0.62).add(phase)).toVar()
      const s2 = sin(tm.mul(0.23).add(phase.mul(1.7)).add(2.1)).toVar()
      const s3 = cos(tm.mul(0.41).add(phase.mul(0.6))).toVar()
      const yaw = sin(tm.mul(0.11).add(phase)).mul(0.55).toVar()
      const d = vec2(
        dir.x.mul(cos(yaw)).sub(dir.y.mul(sin(yaw))),
        dir.x.mul(sin(yaw)).add(dir.y.mul(cos(yaw))),
      ).toVar()

      const swayMid = vec2(
        cos(tm.mul(0.5).add(phase).add(1.0)),
        sin(tm.mul(0.34).add(phase)),
      ).mul(0.13).mul(len).toVar()
      const swayTip = vec2(
        s1.mul(0.30).add(s2.mul(0.16)),
        s3.mul(0.24).add(s2.mul(0.12)),
      ).mul(len).toVar()

      // The gesture: rise near-vertical, bow out at the hump, drop the
      // tip back toward the water. A quadratic can only make a C, which
      // at these proportions reads as a thorn; the cubic is what makes
      // it an arm. The curl BREATHES: the arm reaches out and draws back
      // in over about half a minute, which is most of the wildness.
      const curl = sin(tm.mul(0.27).add(phase.mul(1.3))).mul(0.5).add(0.5).toVar()
      const out1 = arch.mul(0.45).add(0.22).mul(mix(float(0.55), float(1.25), curl)).toVar()
      const out2 = arch.mul(0.8).add(0.42).mul(mix(float(0.5), float(1.35), curl)).toVar()
      const drop = hook.mul(mix(float(0.35), float(1.0), curl)).toVar()

      const b1 = vec3(
        d.x.mul(len).mul(0.06).add(swayMid.x),
        len.mul(float(0.5).add(s2.mul(0.06))),
        d.y.mul(len).mul(0.06).add(swayMid.y),
      ).toVar()
      const b2 = vec3(
        d.x.mul(len).mul(out1),
        len.mul(float(0.98).add(s2.mul(0.07))),
        d.y.mul(len).mul(out1),
      ).toVar()
      const b3 = vec3(
        d.x.mul(len).mul(out2).add(swayTip.x),
        len.mul(float(0.95).sub(drop.mul(0.62))).add(s3.mul(0.08).mul(len)),
        d.y.mul(len).mul(out2).add(swayTip.y),
      ).toVar()

      const omt = float(1.0).sub(t).toVar()
      const q = b1.mul(omt.mul(omt).mul(t).mul(3.0))
        .add(b2.mul(omt.mul(t).mul(t).mul(3.0)))
        .add(b3.mul(t.mul(t).mul(t)))
        .toVar()
      const dq = b1.mul(omt.mul(omt).mul(3.0))
        .add(b2.sub(b1).mul(omt.mul(t).mul(6.0)))
        .add(b3.sub(b2).mul(t.mul(t).mul(3.0)))
        .toVar()

      // A serpentine wave running out along the arm, sideways to the
      // bow. This is what stops it reading as a bent rod: the arm
      // ripples rather than just leaning. Added in the fixed bend-plane
      // basis (which does not depend on the tangent, so there is no
      // circular reference), and its analytic derivative goes into dq so
      // the frame and the lighting stay honest.
      const perp = vec3(d.y.negate(), 0, d.x).toVar()
      const K = float(5.5)
      const W = float(1.35)
      const ampA = t.mul(t).mul(len).mul(0.115).toVar()
      const ang = t.mul(K).sub(tm.mul(W)).add(phase).toVar()
      const wave = ampA.mul(sin(ang)).toVar()
      // d/dt [ a·t²·sin(kt - wτ) ] = 2a·t·sin(…) + a·t²·k·cos(…)
      const dWave = t.mul(2.0).mul(len).mul(0.115).mul(sin(ang))
        .add(ampA.mul(K).mul(cos(ang))).toVar()
      q.addAssign(perp.mul(wave))
      dq.addAssign(perp.mul(dWave))

      // Frame built from the BEND PLANE rather than a fixed reference,
      // so it can never flip or spin as the tip hooks over: the
      // binormal is the axis the arm bends about, and the normal is
      // whichever way is left in the plane.
      // cross(tan, bin) is the CONCAVE direction the whole way along: it
      // is +d at the root (the arm leans that way) and points straight
      // down at the apex of the hook, which is where the curl closes.
      // cross(bin, tan) is its mirror, the convex back of the arm.
      const tan = normalize(dq).toVar()
      const bin = normalize(cross(vec3(d.x, 0, d.y), tan)).toVar()
      const nrm = (SUCKERS_INSIDE ? cross(tan, bin) : cross(bin, tan)).toVar()

      // Cross-section: the artist's own taper is baked into the mesh, so
      // this is a straight scale, not a profile function.
      const r = len.mul(thick).toVar()

      vNrm.assign(
        nrm.mul(normalLocal.x)
          .add(bin.mul(normalLocal.z))
          .add(tan.mul(normalLocal.y)),
      )

      return base
        .add(vec3(0, uWaterLocal.sub(ROOT_DEPTH), 0))
        .add(q)
        .add(nrm.mul(positionGeometry.x.mul(r)))
        .add(bin.mul(positionGeometry.z.mul(r)))
    })()

    // Pull the primitives out of the GLB. Two of them: the body and the
    // sucker band, each with the artist's own material colour.
    const parts = []
    gltf.scene.traverse((o) => {
      if (!o.isMesh) return
      const src = o.material
      const mat = new THREE.MeshStandardNodeMaterial()
      mat.roughness = src.roughness ?? 0.55
      mat.metalness = 0
      mat.side = THREE.DoubleSide
      // Through a uniform, not vec3(color): a bare THREE.Color does not
      // bind as a node and the whole creature renders black.
      const uCol = uniform(src.color ? src.color.clone() : new THREE.Color('#ffffff'))

      const vNrm = varying(vec3(0, 1, 0), `vTent_${parts.length}`)
      mat.positionNode = makeBend(vNrm)
      mat.normalNode = normalize(vNrm)

      // A little depth into the crevices: darken the deepest part of the
      // arm and where it leaves the water, so the silhouette does not go
      // flat under the dither.
      mat.colorNode = Fn(() => {
        const t = clamp(positionGeometry.y, 0.0, 1.0)
        const wet = mix(float(0.55), float(1.0), smoothstep(0.0, 0.16, t))
        const tipLift = mix(float(1.0), float(1.14), smoothstep(0.55, 1.0, t))
        return vec3(uCol).mul(wet).mul(tipLift)
      })()

      parts.push({ geometry: o.geometry, material: mat })
    })

    // Ripple rings where the arms pierce the surface, riding a whisker
    // above the water and breathing out of phase with their arm.
    const ringGeometry = new THREE.RingGeometry(0.72, 1, 40)
    ringGeometry.rotateX(-Math.PI / 2)

    const ringMaterial = new THREE.MeshBasicNodeMaterial()
    ringMaterial.transparent = true
    ringMaterial.depthWrite = false
    ringMaterial.positionNode = Fn(() => {
      const i = instanceIndex.toVar()
      const { base, len, thick, phase } = armParams(i)
      const s = len.mul(thick).mul(float(0.42).add(sin(time.mul(0.8).add(phase)).mul(0.07)))
      return vec3(base.x, uWaterLocal.add(0.02), base.z)
        .add(vec3(positionGeometry.x.mul(s), 0, positionGeometry.z.mul(s)))
    })()
    ringMaterial.colorNode = vec3(0.82, 0.86, 1.0)
    ringMaterial.opacityNode = Fn(() => {
      const i = instanceIndex.toVar()
      const { phase } = armParams(i)
      const dEdge = positionGeometry.xz.length()
      const band = smoothstep(0.72, 0.82, dEdge).mul(float(1.0).sub(smoothstep(0.9, 1.0, dEdge)))
      return band.mul(float(0.15).add(sin(time.mul(0.9).add(phase)).mul(0.07)))
    })()

    return { parts, uWaterLocal, ringGeometry, ringMaterial }
  }, [gltf])

  // The group sits on the terrain under the ENTITY (the bank); the water
  // plane is at world -0.32. Express that in the group's local space.
  uWaterLocal.value = WATER_WORLD_Y - groundY

  return (
    <group>
      {/* Culling is off because the instances live in the shader: the
          geometry's own bounds are one unit-tall arm at the origin, and
          three would cull the whole creature the moment that left view. */}
      {parts.map((p, i) => (
        <instancedMesh key={i} args={[p.geometry, p.material, COUNT]} frustumCulled={false} />
      ))}
      <instancedMesh args={[ringGeometry, ringMaterial, COUNT]} frustumCulled={false} />
    </group>
  )
}

useGLTF.preload('/models/tentacle.glb')

export default OctopusTentacles
